import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/Functions';

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // CAPTCHA
  const [captchaImg, setCaptchaImg] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const refreshCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaCode('');
    try {
      const res = await fetch(`${API_URL}/api/public/captcha`);
      const data = await res.json();
      if (data.status === 'success') {
        setCaptchaImg(data.image_base64);
        setCaptchaToken(data.captcha_token);
      }
    } catch (e) {}
    finally { setCaptchaLoading(false); }
  }, []);

  useEffect(() => { refreshCaptcha(); }, [refreshCaptcha]);

  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const serviceName = params.get('service');
    if (serviceName) {
      const template = `Hello,\n\nI would like to book the "${serviceName}" service.\n\nPlease contact me at your earliest convenience to discuss the requirements and the next steps to proceed.\n\nLooking forward to hearing from you.`;
      setFormData(prev => ({
        ...prev,
        message: template
      }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    
    if (name === 'full_name') {
      finalValue = value.replace(/[^a-zA-Z\s]/g, '');
    } else if (name === 'phone') {
      // Only allow digits and an optional leading plus
      const cleaned = value.replace(/(?!^)\+/g, '').replace(/[^\d+]/g, '');
      if (cleaned.length > 14) return;
      finalValue = cleaned;
    }
    
    setFormData(prev => ({ ...prev, [name]: finalValue }));

    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (formData.phone && phoneDigits.length < 10) {
      setFieldErrors({ phone: ['Phone number must be at least 10 digits.'] });
      return;
    }
    
    setLoading(true);
    setError(null);
    setFieldErrors({});
    try {
      const res = await fetch(`${API_URL}/api/public/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ ...formData, captcha_token: captchaToken, captcha_code: captchaCode })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.errors) {
          console.log('[DEBUG] Validation errors:', data.errors);
          setFieldErrors(data.errors);
          if (data.errors.captcha_code) await refreshCaptcha();
        } else {
          console.log('[DEBUG] Error response:', data.message);
          setError(data.message || 'Something went wrong. Please try again.');
        }
      } else {
        console.log('[DEBUG] Form Data Stored on DB successfully:', data.data);
        console.log('[DEBUG] Email Status:', data.email_status);
        
        const userName = formData.full_name;
        navigate('/thank-you', { state: { name: userName } });
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={{ fontFamily: "'Inter', sans-serif", margin: 0, padding: 0 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        .ct-hero {
          background-image: linear-gradient(135deg, rgba(6,24,48,0.9) 0%, rgba(10,37,64,0.95) 100%), url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1920');
          background-size: cover;
          background-position: center;
          padding: 80px 0;
          position: relative;
          overflow: hidden;
        }
        .ct-hero::before {
          content: '';
          position: absolute;
          top: -120px; right: -120px;
          width: 600px; height: 600px;
          background: radial-gradient(circle, rgba(207,160,71,0.07) 0%, transparent 70%);
          pointer-events: none;
        }
        .ct-hero::after {
          content: '';
          position: absolute;
          bottom: -180px; left: -100px;
          width: 500px; height: 500px;
          background: radial-gradient(circle, rgba(207,160,71,0.05) 0%, transparent 70%);
          pointer-events: none;
        }
        .ct-hero-inner {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 64px;
          align-items: start;
        }
        .ct-tag {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(207,160,71,0.15);
          border: 1px solid rgba(207,160,71,0.3);
          border-radius: 20px;
          padding: 8px 20px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.8px;
          text-transform: uppercase;
          color: #CFA047;
          margin-bottom: 24px;
        }
        .ct-title {
          font-size: 46px;
          font-weight: 800;
          color: #fff;
          line-height: 1.12;
          margin: 0 0 20px;
          letter-spacing: -1.5px;
        }
        .ct-title span { color: #CFA047; }
        .ct-subtitle {
          font-size: 16px;
          color: rgba(255,255,255,0.6);
          line-height: 1.75;
          margin: 0 0 48px;
          max-width: 440px;
        }
        .ct-info-list { display: flex; flex-direction: column; gap: 20px; }
        .ct-info-item { display: flex; align-items: center; gap: 18px; }
        .ct-info-icon {
          width: 48px; height: 48px;
          border-radius: 14px;
          background: rgba(207,160,71,0.12);
          border: 1px solid rgba(207,160,71,0.25);
          display: flex; align-items: center; justify-content: center;
          color: #CFA047; font-size: 18px; flex-shrink: 0;
        }
        .ct-info-label {
          font-size: 11px; font-weight: 700;
          letter-spacing: 1px; text-transform: uppercase;
          color: rgba(255,255,255,0.35); margin-bottom: 4px;
        }
        .ct-info-value { font-size: 15px; font-weight: 600; color: #fff; }

        /* Form Card */
        .ct-form-card {
          background: #fff;
          border-radius: 24px;
          box-shadow: 0 32px 80px rgba(0,0,0,0.2);
          padding: 48px 44px;
        }
        .ct-form-title {
          font-size: 26px; font-weight: 800; color: #0a192f;
          margin: 0 0 6px; letter-spacing: -0.5px;
        }
        .ct-form-subtitle { font-size: 14px; color: #64748b; margin: 0 0 32px; }
        .ct-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .ct-field { display: flex; flex-direction: column; gap: 7px; }
        .ct-field.span2 { grid-column: 1 / -1; }
        .ct-label { font-size: 11px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.6px; }
        .ct-label em { color: #ef4444; font-style: normal; }
        .ct-input {
          width: 100%; padding: 13px 16px;
          border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-size: 14.5px; font-family: 'Inter', sans-serif;
          color: #0f172a; background: #f8fafc; outline: none;
          transition: all 0.2s; box-sizing: border-box;
        }
        .ct-input:focus { border-color: #CFA047; background: #fff; box-shadow: 0 0 0 3px rgba(207,160,71,0.12); }
        .ct-input.has-error { border-color: #ef4444; background: #fff5f5; }
        .ct-input::placeholder { color: #94a3b8; }
        .ct-input-textarea { resize: vertical; min-height: 120px; }
        .ct-field-error { font-size: 12px; color: #ef4444; font-weight: 500; display: flex; align-items: center; gap: 5px; }

        /* CAPTCHA */
        .ct-captcha-wrap {
          grid-column: 1 / -1;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px 18px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 12px;
        }
        .ct-captcha-wrap.has-error { border-color: #ef4444; }
        .ct-captcha-label { font-size: 11px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.6px; width: 100%; }
        .ct-captcha-label em { color: #ef4444; font-style: normal; }
        .ct-captcha-img-box {
          display: flex; align-items: center; gap: 8px;
          background: #fff; border: 1px dashed #cbd5e1;
          border-radius: 8px; padding: 6px 12px;
        }
        .ct-captcha-img { height: 40px; user-select: none; pointer-events: none; transition: opacity 0.2s; }
        .ct-captcha-refresh {
          background: none; border: none; color: #64748b; cursor: pointer;
          font-size: 15px; padding: 6px 8px; border-radius: 6px; transition: color 0.2s, transform 0.3s;
        }
        .ct-captcha-refresh:hover { color: #0a2540; }
        .ct-captcha-refresh:active { transform: rotate(180deg); }
        .ct-captcha-refresh:disabled { opacity: 0.4; cursor: not-allowed; }
        .ct-captcha-input {
          padding: 10px 14px;
          border: 1.5px solid #e2e8f0; border-radius: 9px;
          font-size: 15px; width: 160px; outline: none;
          background: #fff; letter-spacing: 3px; font-family: monospace;
          transition: border-color 0.2s; box-sizing: border-box;
        }
        .ct-captcha-input:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207,160,71,0.12); }
        .ct-captcha-input.has-error { border-color: #ef4444; }
        .ct-captcha-input::placeholder { letter-spacing: 0; font-family: 'Inter', sans-serif; color: #94a3b8; }

        .ct-alert {
          grid-column: 1 / -1;
          background: #fff5f5; border: 1.5px solid #fecaca; border-radius: 10px;
          padding: 14px 18px; color: #dc2626; font-size: 13.5px; font-weight: 500;
          display: flex; align-items: center; gap: 10px;
        }
        .ct-submit-btn {
          grid-column: 1 / -1; width: 100%;
          padding: 16px 32px;
          background: linear-gradient(135deg, #061830 0%, #0a2540 100%);
          color: #fff; border: none; border-radius: 12px;
          font-size: 15px; font-weight: 700; font-family: 'Inter', sans-serif;
          cursor: pointer; transition: all 0.25s;
          display: flex; align-items: center; justify-content: center; gap: 10px;
          box-shadow: 0 6px 20px rgba(6,24,48,0.2);
        }
        .ct-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          background: linear-gradient(135deg, #CFA047 0%, #b8860b 100%);
          box-shadow: 0 10px 28px rgba(207,160,71,0.3);
        }
        .ct-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }

        /* Success */
        .ct-success { text-align: center; padding: 24px 0; }
        .ct-success-icon {
          width: 80px; height: 80px;
          background: linear-gradient(135deg, #ecfdf5, #d1fae5);
          border: 3px solid #6ee7b7; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 32px; color: #059669; margin: 0 auto 24px;
        }
        .ct-success-title { font-size: 24px; font-weight: 800; color: #0f172a; margin: 0 0 10px; }
        .ct-success-text { font-size: 15px; color: #64748b; line-height: 1.65; margin: 0 0 28px; }
        .ct-success-back {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 13px 28px;
          background: linear-gradient(135deg, #061830, #0a2540);
          color: #fff; border: none; border-radius: 50px;
          font-size: 14px; font-weight: 700; cursor: pointer;
          font-family: 'Inter', sans-serif; transition: all 0.2s;
        }
        .ct-success-back:hover { background: linear-gradient(135deg, #CFA047, #b8860b); }

        @media (max-width: 991px) {
          .ct-hero-inner { grid-template-columns: 1fr; gap: 48px; }
          .ct-title { font-size: 36px; }
          .ct-form-card { padding: 36px 28px; }
        }
        @media (max-width: 575px) {
          .ct-hero { padding: 60px 0; }
          .ct-title { font-size: 30px; }
          .ct-form-grid { grid-template-columns: 1fr; }
          .ct-field.span2 { grid-column: auto; }
          .ct-captcha-wrap { grid-column: auto; }
          .ct-alert { grid-column: auto; }
          .ct-submit-btn { grid-column: auto; }
          .ct-form-card { padding: 28px 20px; }
        }
      `}</style>

      <div className="ct-hero">
        <div className="container">
          <div className="ct-hero-inner">

            {/* Left — Info */}
            <div>
              <div className="ct-tag">
                <i className="fa-solid fa-headset"></i>
                Get In Touch
              </div>
              <h1 className="ct-title">
                Let's Talk About<br /><span>Your Business</span>
              </h1>
              <p className="ct-subtitle">
                Our expert team is ready to help you navigate tax, compliance, and legal services. Reach out — we respond within 24 hours.
              </p>
              <div className="ct-info-list">
                <div className="ct-info-item">
                  <div className="ct-info-icon"><i className="fa-solid fa-phone"></i></div>
                  <div>
                    <div className="ct-info-label">Call Us</div>
                    <div className="ct-info-value">+91 8608680756</div>
                  </div>
                </div>
                <div className="ct-info-item">
                  <div className="ct-info-icon"><i className="fa-solid fa-envelope"></i></div>
                  <div>
                    <div className="ct-info-label">Email Us</div>
                    <div className="ct-info-value">
                      <a href="mailto:help@legalinfos.in" className="text-white text-decoration-none">help@legalinfos.in</a>
                    </div>
                  </div>
                </div>
                <div className="ct-info-item">
                  <div className="ct-info-icon"><i className="fa-solid fa-location-dot"></i></div>
                  <div>
                    <div className="ct-info-label">Visit Us</div>
                    <div className="ct-info-value">45/2 ek guru street periamet chennai 600 003</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div className="ct-form-card">
                  <h2 className="ct-form-title">Send Us a Message</h2>
                  <p className="ct-form-subtitle">Fill out the form and we'll get back to you shortly.</p>
                  <form onSubmit={handleSubmit}>
                    <div className="ct-form-grid">

                      <div className="ct-field">
                        <label className="ct-label">Full Name <em>*</em></label>
                        <input
                          type="text" name="full_name"
                          className={`ct-input${fieldErrors.full_name ? ' has-error' : ''}`}
                          placeholder="e.g. Amit Sharma"
                          value={formData.full_name} onChange={handleChange} required
                        />
                        {fieldErrors.full_name && <span className="ct-field-error"><i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.full_name[0]}</span>}
                      </div>

                      <div className="ct-field">
                        <label className="ct-label">Phone Number</label>
                        <input
                          type="tel" name="phone"
                          className={`ct-input${fieldErrors.phone ? ' has-error' : ''}`}
                          placeholder="+91 8608680756"
                          value={formData.phone} onChange={handleChange}
                        />
                        {fieldErrors.phone && <span className="ct-field-error"><i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.phone[0]}</span>}
                      </div>

                      <div className="ct-field span2">
                        <label className="ct-label">Email Address</label>
                        <input
                          type="email" name="email"
                          className={`ct-input${fieldErrors.email ? ' has-error' : ''}`}
                          placeholder="you@example.com"
                          value={formData.email} onChange={handleChange}
                        />
                        {fieldErrors.email && <span className="ct-field-error"><i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.email[0]}</span>}
                      </div>

                      <div className="ct-field span2">
                        <label className="ct-label">Your Message <em>*</em></label>
                        <textarea
                          name="message"
                          className={`ct-input ct-input-textarea${fieldErrors.message ? ' has-error' : ''}`}
                          placeholder="Tell us how we can help you…"
                          value={formData.message} onChange={handleChange} required
                        />
                        {fieldErrors.message && <span className="ct-field-error"><i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.message[0]}</span>}
                      </div>

                      {/* CAPTCHA */}
                      <div className={`ct-captcha-wrap${fieldErrors.captcha_code ? ' has-error' : ''}`}>
                        <div className="ct-captcha-label">Security Check <em>*</em></div>
                        <div className="ct-captcha-img-box">
                          {captchaImg
                            ? <img src={captchaImg} alt="captcha" className="ct-captcha-img" style={{ opacity: captchaLoading ? 0.3 : 1 }} />
                            : <div style={{ height: 40, width: 130, background: '#e2e8f0', borderRadius: 4 }} />
                          }
                          <button
                            type="button" className="ct-captcha-refresh"
                            onClick={refreshCaptcha} disabled={captchaLoading}
                            title="Refresh captcha"
                          >
                            <i className={`fa-solid fa-arrows-rotate${captchaLoading ? ' fa-spin' : ''}`}></i>
                          </button>
                        </div>
                        <input
                          type="text"
                          className={`ct-captcha-input${fieldErrors.captcha_code ? ' has-error' : ''}`}
                          placeholder="Enter code"
                          value={captchaCode}
                          onChange={e => { setCaptchaCode(e.target.value); if (fieldErrors.captcha_code) setFieldErrors(p => ({ ...p, captcha_code: null })); }}
                          required autoComplete="off"
                        />
                        {fieldErrors.captcha_code && (
                          <span className="ct-field-error" style={{ width: '100%' }}>
                            <i className="fa-solid fa-circle-exclamation"></i>{fieldErrors.captcha_code[0]}
                          </span>
                        )}
                      </div>

                      {error && (
                        <div className="ct-alert">
                          <i className="fa-solid fa-triangle-exclamation"></i>{error}
                        </div>
                      )}

                      <button type="submit" className="ct-submit-btn" disabled={loading}>
                        {loading
                          ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Sending…</>
                          : <>Send Message <i className="fa-solid fa-paper-plane"></i></>
                        }
                      </button>

                    </div>
                  </form>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
